/** How much darker a shape's outline is than its fill (share of each RGB channel removed). */
export const OUTLINE_DARKEN = 0.25;

/** Outline of a shape whose fill is fully transparent: darkening nothing would leave it invisible. */
export const TRANSPARENT_FILL_OUTLINE = '#555';

const TRANSPARENT = 'transparent';

const MAX_CHANNEL = 255;
const HEX_RADIX = 16;
const SHORT_HEX = /^#([0-9a-f])([0-9a-f])([0-9a-f])$/i;
const LONG_HEX = /^#([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i;
const RGB = /^rgba?\(\s*(\d+(?:\.\d+)?)\s*,\s*(\d+(?:\.\d+)?)\s*,\s*(\d+(?:\.\d+)?)\s*(?:,\s*(\d*(?:\.\d+)?)\s*)?\)$/i;

type Rgba = { r: number; g: number; b: number; a: number };

/** Parses the colour formats the app stores in `nodeBg`: `#rgb`, `#rrggbb`, `rgb(…)`, `rgba(…)` and `transparent`. */
export const parseColor = (color: string): Rgba | null => {
  const value = color.trim();
  if (value.toLowerCase() === TRANSPARENT) return { r: 0, g: 0, b: 0, a: 0 };
  const short = SHORT_HEX.exec(value);
  if (short) {
    const [r, g, b] = short.slice(1).map((digit) => parseInt(digit + digit, HEX_RADIX));
    return { r, g, b, a: 1 };
  }
  const long = LONG_HEX.exec(value);
  if (long) {
    const [r, g, b] = long.slice(1).map((pair) => parseInt(pair, HEX_RADIX));
    return { r, g, b, a: 1 };
  }
  const rgb = RGB.exec(value);
  if (!rgb) return null;
  return { r: Number(rgb[1]), g: Number(rgb[2]), b: Number(rgb[3]), a: rgb[4] ? Number(rgb[4]) : 1 };
};

const channel = (value: number, keep: number) => Math.round(Math.min(Math.max(value * keep, 0), MAX_CHANNEL));

/** `color` with `amount` (0–1) of each RGB channel removed, keeping alpha; unknown formats come back unchanged. */
export const darkenColor = (color: string, amount: number): string => {
  const parsed = parseColor(color);
  if (!parsed) return color;
  const keep = 1 - amount;
  return `rgba(${channel(parsed.r, keep)}, ${channel(parsed.g, keep)}, ${channel(parsed.b, keep)}, ${parsed.a})`;
};

/** Outline for a fill: the fill darkened, or a neutral grey when the fill is fully transparent. */
export const getOutlineColor = (fill: string): string =>
  (parseColor(fill)?.a === 0 ? TRANSPARENT_FILL_OUTLINE : darkenColor(fill, OUTLINE_DARKEN));
